/**
 * Thin facade over the signed-in user's permission payload.
 * It keeps permission lookups readable inside components and guards while delegating
 * the actual screen-access rules to the centralized RBAC config.
 */
import { Injectable, computed, inject } from '@angular/core';

import { AuthService } from '../../features/login/auth.service';
import { AccessKey, canSatisfyAccessRequirement } from './access-control.config';
import { PermissionCode } from './permission-codes';

@Injectable({ providedIn: 'root' })
export class PermissionsService {
  private readonly authService = inject(AuthService);

  // Derived directly from the live auth profile so permission changes propagate consistently.
  readonly grantedPermissions = computed(() => this.authService.currentUser()?.permissions ?? []);
  readonly currentUserRole = computed(() => this.authService.currentUser()?.role);

  /**
   * Returns whether the current user holds one specific backend permission code.
   */
  hasPermission(permission: PermissionCode): boolean {
    return this.grantedPermissions().includes(permission);
  }

  hasAnyPermission(permissions: readonly PermissionCode[]): boolean {
    const granted = this.grantedPermissions();
    return permissions.some((permission) => granted.includes(permission));
  }

  hasAllPermissions(permissions: readonly PermissionCode[]): boolean {
    const granted = this.grantedPermissions();
    return permissions.every((permission) => granted.includes(permission));
  }

  /**
   * Resolves one screen or module access key through the shared RBAC matrix.
   */
  canAccess(accessKey: AccessKey): boolean {
    return canSatisfyAccessRequirement(this.grantedPermissions(), this.currentUserRole(), accessKey);
  }
}
